import React, { useState, useMemo } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import { XMarkIcon, EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline"
import { useStyleGeneration } from "../hooks/useStyleGeneration"
import { useCSSClassManager } from "../../hooks/useCSSClassManager"

const ClassPreview = ({
  selectedClasses = [],
  disabledClasses = new Set(),
  onEditClass,
  onRemoveFromSelection,
  onToggleDisabled
}) => {
  const { generateClassesCSS } = useStyleGeneration()
  const cssManager = useCSSClassManager()

  // Parse class name to detect modifier
  const parseClassName = useMemo(() => (className) => {
    if (!className) return { baseName: '', modifier: '', modifierValue: '' }
    const parts = className.split(':')
    if (parts.length === 1) {
      return { baseName: parts[0], modifier: '', modifierValue: '' }
    }
    const baseName = parts[0]
    const modifierPart = parts[1]
    const parenthesesMatch = modifierPart.match(/^(.+?)\((.+)\)$/)
    if (parenthesesMatch) {
      return { baseName, modifier: parenthesesMatch[1], modifierValue: parenthesesMatch[2] }
    }
    return { baseName, modifier: modifierPart, modifierValue: '' }
  }, [])

  // Generate preview CSS only (no inline styles)
  const previewCSS = useMemo(() => {
    if (selectedClasses.length === 0) return ''

    // Get the most up-to-date class data from the centralized manager
    const upToDateSelectedClasses = selectedClasses.map(selectedClass => {
      const upToDateClass = cssManager.getClass(selectedClass.name)
      return upToDateClass || selectedClass // Fallback to original if not found
    })

    // Generate CSS for all classes, but comment out disabled ones
    const enabledClasses = upToDateSelectedClasses.filter(cls => !disabledClasses.has(cls.name))
    const disabledClassesArray = upToDateSelectedClasses.filter(cls => disabledClasses.has(cls.name))

    let css = generateClassesCSS(enabledClasses)

    // Add commented out CSS for disabled classes
    if (disabledClassesArray.length > 0) {
      const disabledCSS = generateClassesCSS(disabledClassesArray)
      const commentedCSS = disabledCSS
        .split('\n')
        .map(line => line.trim() ? `/* ${line} */` : line)
        .join('\n')
      css += '\n\n/* Disabled Classes */\n' + commentedCSS
    }

    return css
  }, [selectedClasses, disabledClasses, generateClassesCSS, cssManager.classes])

  // Toggle disabled state for a class
  const handleToggleDisabled = (className) => {
    onToggleDisabled?.(className)
  }

  return (
    <div className="w-1/3 border-r border-admin_bordered p-6 flex flex-col">
      {/* Add CSS for all classes */}
      {previewCSS && (
        <style dangerouslySetInnerHTML={{ __html: previewCSS }} />
      )}

      <Typography variant="admin_h5" className="mb-4">
        Style Preview
      </Typography>


      {/* Preview Box */}
      <div className="flex-1 min-h-[400px] flex items-center justify-center bg-admin_canvas border-2 border-dashed border-admin_bordered rounded-lg p-8">
        <div className="w-40 h-40 flex items-center justify-center relative">
          <div
            className={`w-32 h-32 bg-admin_neutral_1 border border-admin_bordered rounded-lg flex items-center justify-center text-admin_text_secondary transition-all duration-200 p-4 min-w-[8rem] min-h-[8rem] max-w-none max-h-none cursor-pointer ${selectedClasses.filter(cls => !disabledClasses.has(cls.name)).map(cls => {
              const { baseName } = parseClassName(cls.name)
              return baseName
            }).join(' ')}`}
            tabIndex={0}
          >
            <span className="pointer-events-none select-none">
              Preview
            </span>
          </div>
        </div>
      </div>

      {/* Applied Classes List */}
      {selectedClasses.length > 0 && (
        <div className="mt-4">
          <Typography variant="admin_body_small" className="mb-2 text-admin_text_secondary">
            Applied Classes:
          </Typography>
          <div className="flex flex-wrap gap-1">
            {selectedClasses.map((cls, index) => {
              const isDisabled = disabledClasses.has(cls.name)
              const { modifier } = parseClassName(cls.name)
              const hasModifier = modifier && modifier !== ''

              return (
                <span
                  key={`${cls.name}-${index}`}
                  className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded transition-all ${
                    isDisabled
                      ? 'bg-admin_neutral_2 text-admin_text_secondary opacity-50'
                      : 'bg-admin_primary text-white'
                  }`}
                >
                  {/* Toggle visibility button */}
                  <button
                    onClick={() => handleToggleDisabled(cls.name)}
                    className="hover:bg-white hover:bg-opacity-20 rounded p-0.5"
                    title={isDisabled ? 'Enable preview' : 'Disable preview'}
                  >
                    {isDisabled ? (
                      <EyeSlashIcon className="h-3 w-3" />
                    ) : (
                      <EyeIcon className="h-3 w-3" />
                    )}
                  </button>

                  {/* Class name - click to edit */}
                  <button
                    onClick={() => onEditClass?.(cls)}
                    className="hover:underline flex items-center gap-1"
                    title="Edit class properties"
                  >
                    .{cls.name}
                    {hasModifier && (
                      <span className="text-xs opacity-75" title={`Interactive: ${modifier}`}>
                        ⚡
                      </span>
                    )}
                  </button>

                  {/* Remove from selection button */}
                  <button
                    onClick={() => onRemoveFromSelection?.(cls)}
                    className="hover:bg-white hover:bg-opacity-20 rounded p-0.5"
                    title="Remove class"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              )
            })}
          </div>
        </div>
      )}

      {selectedClasses.length === 0 && (
        <div className="mt-4 text-center">
          <Typography variant="admin_body_small" className="text-admin_text_secondary">
            Select classes from the table to preview their combined styles
          </Typography>
        </div>
      )}
    </div>
  )
}

export default ClassPreview